import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { formatCurrency } from "@/lib/config";
import { ValeurIndisponible, QB_DECONNECTE } from "@/components/dashboard/ValeurIndisponible";

export function SectionTresorerie({ cashflow, cashSolde, taxes, debtRatio, deptView = 'global', qbDiag }) {
  // 📊 V2 : la trésorerie (QuickBooks) n'est pas ventilée par département → mention explicite plutôt que section masquée.
  const qbOff = Boolean(qbDiag && !qbDiag.connecte);
  const qbRaison = `${QB_DECONNECTE}${qbDiag?.raison ? ' — ' + qbDiag.raison : ''}`;
  const QB = ({ v, testid }) => qbOff ? <ValeurIndisponible raison={qbRaison} action="Reconnecter" lienAction="/quickbooks" testid={testid} compact /> : formatCurrency(v);
  return (
    <>
      <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2" data-testid="tresorerie-titre">
        Trésorerie (QuickBooks){deptView !== 'global' && <span className="normal-case font-normal text-slate-400" data-testid="tresorerie-mention-globale">— vue globale seulement (non ventilée par département)</span>}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-blue-700 font-semibold uppercase tracking-wide">Solde bancaire</p>
            <p className="text-xl font-bold text-blue-800 mt-1"><QB v={cashflow?.solde_final} testid="treso-solde" /></p>
            <p className="text-xs text-gray-500 mt-1">Variation: {formatCurrency(cashflow?.variation, true)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-purple-700 font-semibold uppercase tracking-wide">Argent cash</p>
            <p className="text-xl font-bold text-purple-800 mt-1">{formatCurrency(cashSolde)}</p>
            <p className="text-xs text-gray-500 mt-1">Caisse physique</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Taxes nettes à payer</p>
            <p className="text-xl font-bold text-amber-800 mt-1"><QB v={taxes.solde_net} testid="treso-taxes" /></p>
            <p className="text-xs text-gray-500 mt-1">TPS+TVQ perçues − payées</p>
          </CardContent>
        </Card>
      </div>

      {/* Ratio d'endettement */}
      {debtRatio && debtRatio.revenuMensuel > 0 && (() => {
        const { totalMonthlyPayments, revenuMensuel, refYear } = debtRatio;
        const ratioPct = (totalMonthlyPayments / revenuMensuel) * 100;
        const isGreen = ratioPct < 15;
        const isYellow = ratioPct >= 15 && ratioPct < 25;
        const isRed = ratioPct >= 25;
        const barWidth = Math.min(100, ratioPct);
        return (
          <Card data-testid="debt-ratio-widget" className={`border ${isRed ? 'border-red-200 bg-red-50/40' : isYellow ? 'border-amber-200 bg-amber-50/40' : 'border-green-200 bg-green-50/40'}`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-4 h-4 shrink-0 ${isRed ? 'text-red-600' : isYellow ? 'text-amber-600' : 'text-green-600'}`} />
                  <div>
                    <p className={`text-sm font-semibold ${isRed ? 'text-red-700' : isYellow ? 'text-amber-700' : 'text-green-700'}`}>
                      Ratio d&apos;endettement — {ratioPct.toFixed(1)}% des revenus mensuels
                    </p>
                    <p className={`text-xs mt-0.5 ${isRed ? 'text-red-600' : isYellow ? 'text-amber-600' : 'text-green-600'}`}>
                      {formatCurrency(totalMonthlyPayments)}/mois en remboursements ÷ {formatCurrency(revenuMensuel)}/mois (moy. {refYear})
                      &nbsp;·&nbsp;
                      {isRed ? "Attention : niveau d'endettement élevé !" : isYellow ? 'Surveiller : approche du seuil critique (25%)' : 'Sain : bien en dessous du seuil (25%)'}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0 hidden sm:block">
                  <p className="text-xs text-slate-500">Seuils : 🟢 &lt;15% / 🟡 15-25% / 🔴 &gt;25%</p>
                </div>
              </div>
              <div className="w-full bg-white rounded-full h-3 border border-slate-200 overflow-hidden relative">
                <div className="absolute top-0 left-[15%] h-full w-px bg-amber-400 opacity-60 z-10" />
                <div className="absolute top-0 left-[25%] h-full w-px bg-red-400 opacity-60 z-10" />
                <div className={`h-full rounded-full transition-all duration-500 ${isRed ? 'bg-red-500' : isYellow ? 'bg-amber-400' : 'bg-green-500'}`}
                  style={{ width: `${barWidth}%` }} />
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </>
  );
}
